import React from 'react';
import { CURRENCIES, LEASE_TYPES, FURNISHING_STATUS } from '../../../constants/propertyConstants';

const Step3PricingTerms = ({ formData, onChange, errors }) => {
    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-soil-900 mb-4">Pricing & Terms</h3>
                <p className="text-sm text-soil-600 mb-6">
                    Set your rental pricing and lease terms
                </p>
            </div>

            {/* Currency */}
            <div>
                <label className="block text-sm font-medium text-soil-600 mb-2">
                    Currency <span className="text-murram-500">*</span>
                </label>
                <select
                    value={formData.currency || 'KES'}
                    onChange={(e) => onChange('currency', e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500 ${
                        errors.currency ? 'border-murram-500' : 'border-soil-100'
                    }`}
                >
                    {CURRENCIES.map((currency) => (
                        <option key={currency.value} value={currency.value}>
                            {currency.label}
                        </option>
                    ))}
                </select>
                {errors.currency && (
                    <p className="mt-1 text-sm text-murram-600">{errors.currency}</p>
                )}
            </div>

            {/* Monthly Rent */}
            <div>
                <label className="block text-sm font-medium text-soil-600 mb-2">
                    Monthly Rent <span className="text-murram-500">*</span>
                </label>
                <div className="relative">
                    <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.pricePerMonth || ''}
                        onChange={(e) => onChange('pricePerMonth', e.target.value ? parseFloat(e.target.value) : null)}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500 ${
                            errors.pricePerMonth ? 'border-murram-500' : 'border-soil-100'
                        }`}
                        placeholder="75000"
                    />
                    <span className="absolute right-3 top-2 text-soil-400">
                        {CURRENCIES.find(c => c.value === (formData.currency || 'KES'))?.symbol}
                    </span>
                </div>
                {errors.pricePerMonth && (
                    <p className="mt-1 text-sm text-murram-600">{errors.pricePerMonth}</p>
                )}
            </div>

            {/* Security Deposit & Service Charge */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-soil-600 mb-2">
                        Security Deposit
                    </label>
                    <div className="relative">
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.securityDeposit || ''}
                            onChange={(e) => onChange('securityDeposit', e.target.value ? parseFloat(e.target.value) : null)}
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500 ${
                                errors.securityDeposit ? 'border-murram-500' : 'border-soil-100'
                            }`}
                            placeholder="150000"
                        />
                        <span className="absolute right-3 top-2 text-soil-400">
                            {CURRENCIES.find(c => c.value === (formData.currency || 'KES'))?.symbol}
                        </span>
                    </div>
                    {errors.securityDeposit && (
                        <p className="mt-1 text-sm text-murram-600">{errors.securityDeposit}</p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-soil-600 mb-2">
                        Service Charge
                    </label>
                    <div className="relative">
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.serviceCharge || ''}
                            onChange={(e) => onChange('serviceCharge', e.target.value ? parseFloat(e.target.value) : null)}
                            className="w-full px-4 py-2 border border-soil-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500"
                            placeholder="5000"
                        />
                        <span className="absolute right-3 top-2 text-soil-400">
                            {CURRENCIES.find(c => c.value === (formData.currency || 'KES'))?.symbol}
                        </span>
                    </div>
                </div>
            </div>

            {/* Lease Type */}
            <div>
                <label className="block text-sm font-medium text-soil-600 mb-2">
                    Lease Type <span className="text-murram-500">*</span>
                </label>
                <select
                    value={formData.leaseType || ''}
                    onChange={(e) => onChange('leaseType', e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500 ${
                        errors.leaseType ? 'border-murram-500' : 'border-soil-100'
                    }`}
                >
                    <option value="">Select lease type</option>
                    {LEASE_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                            {type.label} - {type.description}
                        </option>
                    ))}
                </select>
                {errors.leaseType && (
                    <p className="mt-1 text-sm text-murram-600">{errors.leaseType}</p>
                )}
            </div>

            {/* Minimum Lease Term */}
            <div>
                <label className="block text-sm font-medium text-soil-600 mb-2">
                    Minimum Lease Term (months) <span className="text-murram-500">*</span>
                </label>
                <input
                    type="number"
                    min="1"
                    value={formData.minimumLeaseTerm || ''}
                    onChange={(e) => onChange('minimumLeaseTerm', e.target.value ? parseInt(e.target.value) : null)}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500 ${
                        errors.minimumLeaseTerm ? 'border-murram-500' : 'border-soil-100'
                    }`}
                    placeholder="6"
                />
                {errors.minimumLeaseTerm && (
                    <p className="mt-1 text-sm text-murram-600">{errors.minimumLeaseTerm}</p>
                )}
            </div>

            {/* Furnishing Status */}
            <div>
                <label className="block text-sm font-medium text-soil-600 mb-2">
                    Furnishing Status <span className="text-murram-500">*</span>
                </label>
                <select
                    value={formData.furnishingStatus || ''}
                    onChange={(e) => onChange('furnishingStatus', e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500 ${
                        errors.furnishingStatus ? 'border-murram-500' : 'border-soil-100'
                    }`}
                >
                    <option value="">Select furnishing status</option>
                    {FURNISHING_STATUS.map((status) => (
                        <option key={status.value} value={status.value}>
                            {status.label} - {status.description}
                        </option>
                    ))}
                </select>
                {errors.furnishingStatus && (
                    <p className="mt-1 text-sm text-murram-600">{errors.furnishingStatus}</p>
                )}
            </div>

            {/* Pet & Smoking Policy */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-3 p-4 border border-soil-100 rounded-lg">
                    <input
                        type="checkbox"
                        id="petFriendly"
                        checked={formData.petFriendly || false}
                        onChange={(e) => onChange('petFriendly', e.target.checked)}
                        className="h-5 w-5 text-highland-500 focus:ring-highland-500 border-soil-100 rounded"
                    />
                    <label htmlFor="petFriendly" className="text-sm font-medium text-soil-600 cursor-pointer">
                        Pet Friendly
                    </label>
                </div>

                <div className="flex items-center space-x-3 p-4 border border-soil-100 rounded-lg">
                    <input
                        type="checkbox"
                        id="smokingAllowed"
                        checked={formData.smokingAllowed || false}
                        onChange={(e) => onChange('smokingAllowed', e.target.checked)}
                        className="h-5 w-5 text-highland-500 focus:ring-highland-500 border-soil-100 rounded"
                    />
                    <label htmlFor="smokingAllowed" className="text-sm font-medium text-soil-600 cursor-pointer">
                        Smoking Allowed
                    </label>
                </div>
            </div>

            <div className="bg-highland-50 border border-highland-100 rounded-lg p-4">
                <p className="text-sm text-highland-600">
                    <strong>Pricing Tip:</strong> Research similar properties in your area to set competitive rates.
                </p>
            </div>
        </div>
    );
};

export default Step3PricingTerms;
